import Spinner from "../../../../components/Spinner.jsx";
import {useTranslation} from "react-i18next";
import {useContext, useEffect, useState} from "react";
import {GlobalContext} from "../../../../utils/context/GlobalContextProvider.jsx";
import Button from "../../../../components/Button.jsx";
import Select from "react-select";
import useDownloadState from "../../../../api/utilities/useDownloadState.js";
import LoadingComponent from "../../../../components/LoadingComponent.jsx";
import {ProjectIntegrationsApi} from "../../../../api/authority/generated/index";
import googleLogo from "../../../../images/login/g.svg";
import {IntegrationActions, IntegrationConnectButton, IntegrationSection, OAuthConnectionStatus} from "../IntegrationDetailUi.jsx";
import {isGoogleAdsOAuthReconnectRequired} from "../googleAdsOAuthStatus.js";

export function getGoogleCustomerClientChoices(customerClients = []) {
    const defaultValues = [];
    const options = [];
    const available = [];
    const unavailable = [];

    for (const customerClient of customerClients) {
        const option = {
            customerId: customerClient.customerId,
            customerClientId: customerClient.customerClientId,
            value: customerClient.customerId + customerClient.customerClientId,
            label: '[' + customerClient.customerClientId + '] ' + customerClient.name
        };

        options.push(option);

        if (customerClient.availabilityReason) {
            unavailable.push({...option, availabilityReason: customerClient.availabilityReason});
        } else {
            available.push(option);
        }

        if (customerClient.enabled) {
            defaultValues.push(option);
        }
    }

    return {defaultValues, options, available, unavailable};
}

export function getSelectedGoogleCustomerClientIds(selectedCustomerClients) {
    return selectedCustomerClients.map((customerClient) => customerClient.customerClientId);
}

export default function GoogleIntegrationForm({project, projectIntegration, refreshOAuthResources}) {

    let {t} = useTranslation();
    const {
        defaultValues: customerClientDefaultValues,
        options: customerClientOptions,
        available: availableCustomerClients,
        unavailable: unavailableCustomerClients,
    } = getGoogleCustomerClientChoices(projectIntegration?.googleIntegrationCustomerClients ?? []);

    let [selectedCustomerClients, setSelectedCustomerClients] = useState(customerClientDefaultValues);
    useEffect(() => {
        setSelectedCustomerClients(customerClientDefaultValues);
    }, [projectIntegration?.googleIntegrationCustomerClients]);

    const {context, getAuthorityConfiguration, resetCurrentUserProjects} = useContext(GlobalContext);

    let redirectToLogin = () => {
        let oAuthUrl = new URL(import.meta.env.VITE_AUTHORITY_URL);
        oAuthUrl.pathname = '/api/v1/project/integrations/google';

        let query = new URLSearchParams({
            authorization: context.currentIdentity?.token,
            projectId: project.id,
        });

        if (projectIntegration?.id) {
            query.set('integrationId', projectIntegration.id);
        }

        oAuthUrl.search = query.toString();

        window.location.href = oAuthUrl.toString();
    };

    useEffect(() => {
        if (!projectIntegration) {
            setTimeout(
                () => redirectToLogin(),
                600, // so it looks we actually do something too :D
            )
        }
    }, []);

    let [state, setDownloading] = useDownloadState();

    let saveCustomerClients = () => {

        const customerClientIds = getSelectedGoogleCustomerClientIds(selectedCustomerClients);

        setDownloading(true);
        new ProjectIntegrationsApi(getAuthorityConfiguration())
                .chooseGoogleCustomerClients({
                projectId: project.id,
                integrationId: projectIntegration.id,
                requestBody: customerClientIds
            })
            .then(() => {
                resetCurrentUserProjects();
            })
            .finally(() => setDownloading(false))
    };

    const refreshCustomerClients = () => {
        setDownloading(true);
        Promise.resolve(refreshOAuthResources())
            .finally(() => setDownloading(false));
    };

    if (!projectIntegration) {
        return (
            <div>
                <Spinner size={'spinner-large'} className={'mx-auto'}/>
                <p className={'text-center mt-4'}>{t('redirecting')}...</p>
            </div>
        );
    }

    const reconnectRequired = isGoogleAdsOAuthReconnectRequired(projectIntegration);
    const hasNoDiscoveredAccounts = !reconnectRequired && availableCustomerClients.length === 0;

    // noinspection JSValidateTypes
    return (
        <LoadingComponent loading={state.downloading}>
            <div className={'flex flex-col gap-5'}>
                <IntegrationSection
                    title={'Připojení ke Google Ads'}
                    description={t('project.google.oauth-help')}
                >
                    <OAuthConnectionStatus
                        connected={!reconnectRequired}
                        connectedDescription={t('project.google.oauth-connected-help')}
                        action={reconnectRequired && <IntegrationConnectButton logo={googleLogo} logoAlt={'Google'} label={t('project.google.oauth-reconnect-action')} onClick={redirectToLogin}/>}
                    />
                </IntegrationSection>

                <IntegrationSection
                    title={t('project.google.enabled-client-customers')}
                    description={t('project.google.enabled-client-customers-help')}
                >
                    <Select
                        value={selectedCustomerClients}
                        isMulti
                        name="colors"
                        options={customerClientOptions}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder={null}
                        noOptionsMessage={() => t('project.integration-no-options')}
                        onChange={setSelectedCustomerClients}
                    />
                    {unavailableCustomerClients.length > 0 && (
                        <div className={'mt-4 rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-datixo-negative dark:border-red-800 dark:bg-red-950/30 text-datixo-negative'}>
                            <div className={'text-sm font-semibold'}>{t('project.google.unavailable-accounts-title')}</div>
                            <p className={'mt-1 text-xs leading-5 text-datixo-negative'}>{t('project.google.unavailable-accounts-help')}</p>
                            <div className={'mt-3 flex flex-wrap gap-2'}>
                                {unavailableCustomerClients.map((customerClient) => (
                                    <div key={customerClient.value} className={'inline-flex max-w-full items-center rounded-md border border-red-300 bg-white px-2 py-1 text-sm text-datixo-negative dark:border-red-700 dark:bg-red-950/50 text-datixo-negative'}>
                                        <span className={'min-w-0 truncate'}>{customerClient.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    {hasNoDiscoveredAccounts && (
                        <div className={'app-surface mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800'}>
                            Google nevrátil žádný dostupný aktivní klientský účet. Zkontrolujte přihlášený Google účet nebo aktualizujte seznam.
                        </div>
                    )}
                    <div className={'mt-5'}>
                        <IntegrationActions hint={<Button variant={'outline-primary'} className={'integration-secondary-button whitespace-normal text-center'} onClick={refreshCustomerClients}>Aktualizovat seznam</Button>}>
                            <Button className={'button-primary'} onClick={() => saveCustomerClients()}>
                                Uložit výběr
                            </Button>
                        </IntegrationActions>
                    </div>
                </IntegrationSection>

            </div>
        </LoadingComponent>
    );
}
